import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { OpsManagersForEventGQL } from '../../../generated/graphql.generated';
import { quickJsActions } from '../../quickjs/quickjs.actions';
import {
    selectNavigatorActiveEventId,
    selectNavigatorCurrentStepId,
} from '../../quickjs/quickjs.feature';
import { NAVIGATOR_STARTED_SENTINEL } from '../job-helpers';
import { JobToolSubscriptionActions } from '../job-state/job-tool-subscription.actions';

/**
 * The one path by which state from outside a mounted navigator run may move the crew's step.
 *
 * Before #46 was reworked, the navigator page re-derived the crew's step from `selectJob` on every
 * job change. That is shared state — echoes, refetches, optimistic patches and ops all write it —
 * and the crew's position rode on whatever landed there. A `jobChanged` echo describes what is
 * **persisted**, and a crew with a write in flight is legitimately ahead of that: echoes were
 * measured arriving 30-80s late on a throttled run, each accurate about the database and each
 * walking the crew back two or three steps. See `docs/crew-navigator.md`.
 *
 * So that derivation is gone, and this is the deliberate replacement. It is narrow on purpose:
 *
 *   - **`remoteJobUpdated` only.** This session's own writes echo as `localJobUpdated` (every
 *     navigator mutation passes `generateMutationContext()`, including the launch write since #51),
 *     so our own history can no longer reach us at all.
 *   - **The step from `navigatorCurrentStep` only.** `navigatorData` never decides the step. That
 *     matters: the old dedupe key included `navigatorData.stepStartTime`, which is why *every
 *     inventory patch* re-triggered a step derivation. The one thing read from it is the stored
 *     start of the step the echo already names (#53) — see below.
 *   - **Real steps only.** `'started'` is a launch sentinel, not a position.
 *   - **Only when it differs** from what the slice already holds, so a repeated echo is inert.
 *
 * #52 — an ops override and a second crew device are the same signal on the wire: both are simply
 * "not this session". Rather than pretend otherwise, every external move is reported to the crew, so
 * a real ops move is confirmed and a two-device collision stops being silent. The toast itself is
 * raised by `JobNavigatorPageComponent`, not here: `MessageService` is provided on `AppComponent`
 * (`app.component.ts:46`), which is a *component* injector, and effects live in an environment
 * injector that cannot see it. Injecting it here threw `NullInjectorError` at route activation and
 * took the whole navigator page down with it. Occupancy locking is deferred.
 */
export const remoteNavigatorStepOverride = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(JobToolSubscriptionActions.remoteJobUpdated),
        concatLatestFrom(() => [
            store.select(selectNavigatorActiveEventId),
            store.select(selectNavigatorCurrentStepId),
        ]),
        // A navigator has to be mounted for an override to have anywhere to land. The reducer
        // re-checks the event id, but bailing here keeps the effect off every unrelated job echo.
        filter(([ , activeEventId ]) => !!activeEventId),
        map(([ action, activeEventId, heldStepId ]) => {
            const event = action.output?.job?.events?.find((e) => e.id === activeEventId);
            const incomingStep = event?.navigatorCurrentStep;

            if (!incomingStep || incomingStep === NAVIGATOR_STARTED_SENTINEL) { return null; }
            if (incomingStep === heldStepId) { return null; }

            // The stored start of the step ops moved the crew to, written in the same save as the step
            // change (#53): a re-opened step keeps its original start, a newly started one starts at the
            // move. Stamping the echo's arrival instead put that time on screen, and the crew's next tap
            // wrote it over the stored start — a crew sent back to Load lost the first round's time.
            let storedStart: unknown;
            try { storedStart = JSON.parse(event?.navigatorData ?? '{}')?.steps?.[incomingStep]?.startedAt; } catch { /* now */ }

            return {
                eventId: activeEventId,
                stepId: incomingStep,
                stepStartTime: typeof storedStart === 'number' ? storedStart : Date.now(),
            };
        }),
        filter(Boolean),
        map(({ eventId, stepId, stepStartTime }) => quickJsActions.navigatorStepOverridden({
            eventId,
            stepId,
            stepStartTime,
        })),
    );
}, { functional: true, dispatch: true });

/**
 * Requirement: [6750] - Load the day's ops manager when a navigator opens, so the HELP sheet can
 * offer a real number to call.
 *
 * Fetched here rather than passed through the navigator script: the script reaches the app over
 * HTTP from R2 and ships on its own cadence, so anything routed through it would sit dead until
 * that deploy. The duty contact is app chrome, not step content.
 *
 * Failures do NOT empty the list. `opsManagersLoadError` sets `navigatorOpsManagersStale`, and the
 * HELP sheet keeps what it holds under a "may be out of date" note. An empty list renders as "no ops
 * manager assigned for today", which is an assertion — making it after a dropped request states
 * something false and takes away a number that is almost certainly still correct, since a refresh is
 * for the same event, day and zone. Where nothing is held at all, the sheet says the lookup failed
 * rather than that nobody is assigned.
 */
export const loadOpsManagers = createEffect((
    actions$ = inject(Actions),
    opsManagersGQL = inject(OpsManagersForEventGQL),
) => {
    return actions$.pipe(
        ofType(quickJsActions.navigatorOpenedForEvent),
        switchMap(({ eventId }) => opsManagersGQL
            .fetch({ eventId }, { fetchPolicy: 'network-only' })
            .pipe(
                map((res) => quickJsActions.opsManagersLoaded({
                    eventId,
                    opsManagers: (res.data?.opsManagersForEvent ?? []).map((m) => ({
                        name: m.name,
                        phone: m.phone ?? null,
                    })),
                })),
                catchError((error) => of(quickJsActions.opsManagersLoadError({ eventId, error }))),
            )),
    );
}, { functional: true, dispatch: true });
